import axios, { AxiosResponse } from 'axios';

import { SERVER_URL } from 'Constants';
import { UserEntity } from 'Models/Entities';
import { buildUrl } from 'Util/FetchUtils';

/**
 * This endpoint is for a staff member to create a user entity and send a reset password email to the user.
 *
 * When resolved, it returns the id of the newly created user entity.
 */
export function registerForCustomer(user: UserEntity): Promise<AxiosResponse<string>> {
	return axios.post(`${SERVER_URL}/api/register/user-entity-via-staff`, {
		...user.toJSON(),
		//
		// The endpoint is expecting UserEntityRegistrationModel in the body. So the password field is required,
		// but is not used in the endpoint.
		//
		password: 'placeholder',
	});
}

/**
 * Registers a new Staff/Manager/Admin team member. The backend enforces that the
 * caller has a strictly higher role priority than the target role. A temporary
 * password is generated and an activate-account email is sent.
 *
 * Resolves with the new user's id.
 */
export function registerTeamMember(payload: {
	email: string;
	role: 'Staff' | 'Manager' | 'Admin';
	firstName: string;
	lastName: string;
	phone?: string;
	postcode?: string;
}): Promise<AxiosResponse<{ id: string }>> {
	return axios.post(`${SERVER_URL}/api/register/team-member`, payload);
}

export function resendVerification(userId: string): Promise<AxiosResponse<string>> {
	return axios.get(buildUrl(`${SERVER_URL}/api/register/re-send-verification-email`, { userId: userId }));
}

type UserType =
	| 'admin-entity'
	| 'fixed-price-user-entity'
	| 'invoiced-user-entity'
	| 'manager-entity'
	| 'staff-entity'
	| 'user-entity';

interface RegistrationModel {
	registrationModel: any;
	wizardDataToken: string | null;
	voucherCode?: string;
}

export function registerUser(userType: UserType, data?: RegistrationModel) {
	return axios.post(`${SERVER_URL}/api/register/${userType}`, data);
}

interface ConvertGuestModel {
	phone: string;
	email: string;
	password: string;
	firstName: string;
	lastName: string;
}

/**
 * Converts a guest to a full account. The first call answers 202 with code EMAIL_CODE_REQUIRED and emails
 * the guest a code; repeat it with `code` set.
 */
export function convertGuest(data: any, code?: string) {
	return axios.post(`${SERVER_URL}/api/register/convert-guest`, { ...data, code });
}

/**
 * Staff-initiated conversion of a guest to a fully registered user. The guest is promoted as-is:
 * the backend keeps their existing name, generates a temporary password, and emails them an
 * activate-account link to set their own. Only the email is needed to identify the guest; a
 * placeholder password is sent to satisfy the required field on the request model.
 */
export function convertGuestViaStaff(data: { email: string }) {
	return axios.post(`${SERVER_URL}/api/register/convert-guest-via-staff`, {
		email: data.email,
		password: 'placeholder',
	});
}
